import { useEffect, useMemo, useState } from 'react';
import { Trash2, UserPlus, Users } from 'lucide-react';
import {
  Button,
  Card,
  Chip,
  Description,
  Drawer,
  FieldError,
  ListBox,
  Spinner,
  type OverlayTriggerState,
} from '@heroui/react';
import { AsyncButton } from '../../../../components/ui/async-button';
import type {
  Apartment,
  ApartmentFormPayload,
  ApartmentMember,
} from '../../../api/condominiums';
import type { UserWithMemberships } from '../../../api/users';
import { DrawerSelectField, DrawerTextField } from '../../../../components/ui/drawer-form-field';
import { FormSwitch } from '../../../../components/ui/form-switch';
import { drawerBodyClassName, drawerFooterClassName, sideDrawerDialogClassName } from '../../../../components/ui/side-drawer-classes';
import { StatusCallout } from '../../../../components/ui/status-callout';

interface ApartmentFormDrawerProps {
  state: OverlayTriggerState;
  blockId: string | null;
  condominiumName?: string;
  editing: { apartment: Apartment; blockId: string } | null;
  members: ApartmentMember[];
  availableUsers: UserWithMemberships[];
  onSubmit: (payload: ApartmentFormPayload) => Promise<void>;
  onAssociateMember: (blockId: string, apartmentId: string, userId: string) => Promise<void>;
  onDisassociateMember: (
    blockId: string,
    apartmentId: string,
    userId: string,
  ) => Promise<void>;
}

const emptyForm: ApartmentFormPayload = { number: '', floor: '', isActive: true };

function userLabel(user: UserWithMemberships): string {
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName} — ${user.email}`;
  }
  return user.email;
}

export function ApartmentFormDrawer({
  state,
  blockId,
  condominiumName,
  editing,
  members,
  availableUsers,
  onSubmit,
  onAssociateMember,
  onDisassociateMember,
}: ApartmentFormDrawerProps) {
  const [form, setForm] = useState<ApartmentFormPayload>(emptyForm);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [associating, setAssociating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [associateError, setAssociateError] = useState<string | null>(null);

  useEffect(() => {
    if (!state.isOpen) return;
    setForm(
      editing
        ? {
            number: editing.apartment.number,
            floor: editing.apartment.floor ?? '',
            isActive: editing.apartment.isActive,
          }
        : emptyForm,
    );
    setSelectedUserId(null);
    setError(null);
    setAssociateError(null);
    setSubmitting(false);
    setAssociating(false);
  }, [state.isOpen, editing]);

  const usersForAssociation = useMemo(() => {
    const memberIds = new Set(members.map((m) => m.userId));
    return availableUsers.filter((u) => !memberIds.has(u.id));
  }, [availableUsers, members]);

  const isValid = form.number.trim().length > 0;

  const handleSubmit = async () => {
    if (!blockId || !isValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        number: form.number.trim(),
        floor: form.floor?.trim() || undefined,
        isActive: form.isActive,
      });
      state.close();
    } catch (err) {
      let message = 'Não foi possível salvar o apartamento.';
      if (typeof err === 'object' && err !== null && 'message' in err) {
        message = String((err as { message: unknown }).message);
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssociate = async () => {
    if (!editing || !selectedUserId || associating) return;
    setAssociating(true);
    setAssociateError(null);
    try {
      await onAssociateMember(editing.blockId, editing.apartment.id, selectedUserId);
      setSelectedUserId(null);
    } catch (err) {
      const message =
        typeof err === 'object' && err !== null && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Erro ao associar usuário.';
      setAssociateError(message);
    } finally {
      setAssociating(false);
    }
  };

  return (
    <Drawer state={state}>
      <Drawer.Backdrop isDismissable>
        <Drawer.Content placement="right">
          <Drawer.Dialog className={sideDrawerDialogClassName}>
            <Drawer.Header>
              <Drawer.Heading>{editing ? 'Editar apartamento' : 'Novo apartamento'}</Drawer.Heading>
              <Drawer.CloseTrigger />
            </Drawer.Header>
            <Drawer.Body className={drawerBodyClassName}>
              {editing && members.length === 0 ? (
                <StatusCallout status="warning" title="Nenhum morador associado">
                  Associe um morador com perfil completo e membership ativa neste condomínio.
                </StatusCallout>
              ) : null}

              {editing && members.length > 0 ? (
                <Card className="border border-success bg-success/10">
                  <Card.Content className="flex flex-col gap-3 p-4">
                    <div className="flex items-center gap-2">
                      <Users size={18} className="text-success" />
                      <Description className="font-medium text-foreground">
                        Moradores ({members.length})
                      </Description>
                    </div>
                    <div className="flex flex-col gap-2">
                      {members.map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center justify-between rounded-lg bg-surface p-2"
                        >
                          <div>
                            <p className="text-sm font-medium">{member.userName}</p>
                            <p className="text-xs text-muted">{member.userEmail}</p>
                            <Chip size="sm" variant="soft" className="mt-1">
                              {member.role === 'MANAGER' ? 'Gerente' : 'Usuário'}
                            </Chip>
                          </div>
                          <Button
                            isIconOnly
                            variant="ghost"
                            aria-label="Remover morador"
                            onPress={() =>
                              void onDisassociateMember(
                                editing.blockId,
                                editing.apartment.id,
                                member.userId,
                              )
                            }
                          >
                            <Trash2 size={16} className="text-danger" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </Card.Content>
                </Card>
              ) : null}

              {editing ? (
                <div className="flex flex-col gap-2 border-t border-separator pt-4">
                  <Description className="font-medium text-foreground">
                    Associar morador
                  </Description>
                  {usersForAssociation.length === 0 ? (
                    <Description>
                      Nenhum morador disponível
                      {condominiumName ? ` em ${condominiumName}` : ' neste condomínio'}. Só
                      aparecem usuários com perfil completo e membership ativa no condomínio do
                      apartamento (podem já estar em outro apto do mesmo condomínio).
                    </Description>
                  ) : (
                    <>
                      <DrawerSelectField
                        label="Usuário"
                        placeholder="Selecione um usuário"
                        value={selectedUserId}
                        onChange={(value) => setSelectedUserId(value || null)}
                        isDisabled={associating}
                      >
                        <ListBox>
                          {usersForAssociation.map((user) => (
                            <ListBox.Item
                              key={user.id}
                              id={user.id}
                              textValue={userLabel(user)}
                            >
                              {userLabel(user)}
                              <ListBox.ItemIndicator />
                            </ListBox.Item>
                          ))}
                        </ListBox>
                      </DrawerSelectField>
                      {associateError ? <FieldError>{associateError}</FieldError> : null}
                      <Button
                        variant="secondary"
                        isDisabled={!selectedUserId || associating}
                        onPress={handleAssociate}
                      >
                        {associating ? (
                          <Spinner size="sm" />
                        ) : (
                          <>
                            <UserPlus size={16} />
                            Associar morador
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              ) : null}

              <DrawerTextField
                label="Número do apartamento"
                value={form.number}
                placeholder="Ex: 101, 202"
                onChange={(number) => setForm((prev) => ({ ...prev, number }))}
                isDisabled={submitting}
                isInvalid={!!error}
                errorMessage={error ?? undefined}
              />

              <DrawerTextField
                label="Andar (opcional)"
                value={form.floor ?? ''}
                placeholder="Ex: 1º andar, Térreo"
                onChange={(floor) => setForm((prev) => ({ ...prev, floor }))}
                isDisabled={submitting}
              />

              <FormSwitch
                label="Apartamento ativo"
                isSelected={form.isActive ?? true}
                onChange={(checked) => setForm((prev) => ({ ...prev, isActive: checked }))}
                isDisabled={submitting}
              />
            </Drawer.Body>
            <Drawer.Footer className={drawerFooterClassName}>
              <Button variant="ghost" className="flex-1" isDisabled={submitting} onPress={state.close}>
                Cancelar
              </Button>
              <AsyncButton
                variant="primary"
                className="flex-1"
                isPending={submitting}
                isDisabled={!isValid}
                pendingLabel={editing ? 'Salvar' : 'Criar apartamento'}
                onPress={handleSubmit}
              >
                {editing ? 'Salvar' : 'Criar apartamento'}
              </AsyncButton>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}
